import {identity} from 'lodash';
import React, {FC, ReactNode, useMemo} from 'react';
import styled from 'styled-components';

import {globalWindow} from '../../../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {
  authenticateImageNode,
  stripBackgroundAndCSSImages,
} from '../../../../../../libs/shared/core/src/helpers/browser/domHelpers';
import {tryParseUrl} from '../../../../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {sanitizeHtml} from '../../../../../../libs/shared/core/src/helpers/sanitization/html';
import {CleanHtml} from '../../../../../../libs/shared/core/src/helpers/typescript/cleanHtml';
import {AttachmentModel} from '../../../../../../libs/shared/core/src/models/api/attachmentModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {getHasNewSynchronousP3} from '../../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {TimelineVariationsEnum} from '../../../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {LinkTrap} from '../../../../../../libs/shared/ui/src/components/links/linkTrap';
import {LinkTooltips} from '../../../../../../libs/shared/ui/src/components/tooltips/linkTooltips';
import {searchableAttrsBody} from '../../../../../../srcCommon/inboxes/search/searchAttributesHelpers';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {replaceImageNodesWithPlaceholder} from '../../../../../helpers/domPurifyHelpers';
import {AnnotationsHighlightViewerInheritableProps} from '../../../../common/annotation/annotationsHighlightViewer';
import {MessageHtml} from './messageHtml';
import {MessageViewerImageControlsContainer} from './messageViewerImageControlsContainer';

/*
 * Props.
 */

export interface MessageViewerBodyProps extends AnnotationsHighlightViewerInheritableProps {
  trustedHtml: CleanHtml;
  messageId: number;
  attachments: ReadonlyArray<AttachmentModel>;
  isExternalContentHidden: boolean;
  isUserVisitor: boolean;
  httpToken?: string;
  timelineVariation: TimelineVariationsEnum;
  isInbound?: boolean;
  children?: (body: ReactNode) => ReactNode;
}

/*
 * Style.
 */

const StyledEmptyMessage = styled.em`
  font-style: italic;
  color: ${(p) => p.theme.greys.shade60};
`;

/*
 * Component.
 */

export const MessageViewerBody: FC<MessageViewerBodyProps> = (props) => {
  const {
    messageId,
    trustedHtml,
    isExternalContentHidden,
    isUserVisitor,
    httpToken,
    timelineVariation,
    isInbound,
    children = identity,
  } = props;
  const hasNewSynchronousP3 = useAppSelector(getHasNewSynchronousP3);

  const onTrackLink = (href: string) => {
    const url = tryParseUrl(href);
    if (!url) {
      return;
    }
    trackInterfaceEvent(InterfaceEventsEnum.P3_MESSAGE_LINK_CLICK, url.hostname);
  };

  // Find the body to display.
  const displayTrustedHtml: CleanHtml = useMemo(() => {
    if (isExternalContentHidden) {
      return stripExternalContentFromBody(trustedHtml);
    }

    if (isUserVisitor) {
      return authenticateImagesInBody(trustedHtml, httpToken);
    }

    return trustedHtml;
  }, [trustedHtml, httpToken, isExternalContentHidden, isUserVisitor]);

  /*
   * Render.
   */

  const renderContent = () => {
    if (!displayTrustedHtml) {
      return (
        <MessageHtml
          timelineVariation={timelineVariation}
          isInbound={isInbound}
          hasNewSynchronousP3={hasNewSynchronousP3}
        >
          <StyledEmptyMessage>
            <Trans id="message-viewer-empty-message">Empty message</Trans>
          </StyledEmptyMessage>
        </MessageHtml>
      );
    }

    // The wrapper needs a unique ID for the CSS to be correctly applied.
    const messageIdString = (messageId && messageId.toString(36)) || 'new';
    const contentId = `fae_${messageIdString}`;

    return (
      <MessageHtml
        id={contentId}
        trustedHtml={displayTrustedHtml}
        {...searchableAttrsBody}
        timelineVariation={timelineVariation}
        isInbound={isInbound}
        hasNewSynchronousP3={hasNewSynchronousP3}
      />
    );
  };

  return (
    <LinkTooltips>
      <MessageViewerImageControlsContainer {...props}>
        <LinkTrap onTrack={onTrackLink}>{children(renderContent())}</LinkTrap>
      </MessageViewerImageControlsContainer>
    </LinkTooltips>
  );
};

/*
 * Helpers.
 */

function stripExternalContentFromBody(body: CleanHtml): CleanHtml {
  const uponSanitizeElement = (element: Element) => {
    replaceImageNodesWithPlaceholder(element);
    stripBackgroundAndCSSImages(element);
  };

  return sanitizeHtml(body, {
    hooks: {uponSanitizeElement},
  });
}

function authenticateImagesInBody(body: CleanHtml, httpToken: string | undefined): CleanHtml {
  const uponSanitizeElement = (element: Element) => {
    authenticateImageNode(globalWindow, httpToken, element);
  };

  return sanitizeHtml(body, {
    hooks: {uponSanitizeElement},
  });
}
